"use client";

import { useState, useEffect } from "react";
import type { PlanFeatures, PlansOutput } from "@/interfaces/plan";
import { Check, X } from "lucide-react";
import { PlansService } from "@/services/PlansService";
import { numberToMoney } from "@/util/moneyConversion";
import { useAuth0 } from "@auth0/auth0-react";

const FeatureValue = ({ feature }: { feature: PlanFeatures | undefined }) => {
  if (!feature)
    return (
      <span className="text-[#121516]/80 dark:text-white/80">Ilimitado</span>
    );

  if (feature.value === "true") {
    return <Check className="h-5 w-5 text-green-600 mx-auto" />;
  }

  if (feature.value === "false") {
    return <X className="h-5 w-5 text-red-500 mx-auto" />;
  }

  const numValue = Number(feature.value);
  if (!isNaN(numValue)) {
    const additionalText = feature.allowAdditional
      ? ` (+${numberToMoney(feature.additionalPrice || 0)} por adicional)`
      : "";
    return (
      <span className="text-[#121516]/80 dark:text-white/80">{`Até ${
        feature.value
      }${additionalText} ${feature.displayName.toLowerCase()}`}</span>
    );
  }

  if (feature.value === "-") {
    return (
      <span className="text-[#121516]/80 dark:text-white/80">Ilimitado</span>
    );
  }

  return (
    <span className="text-[#121516]/80 dark:text-white/80">
      {feature.value}
    </span>
  );
};

const MobilePlanCard = ({
  plan,
  allFeatures,
}: {
  plan: PlansOutput;
  allFeatures: string[];
  index: number;
}) => {
  const { loginWithRedirect } = useAuth0();

  return (
    <div
      key={plan.id}
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 transition-all duration-500 transform opacity-100 translate-y-0`}
    >
      <h3 className="text-2xl font-bold text-[#121516] dark:text-white mb-2">
        {plan.name}
      </h3>
      <p className="text-3xl font-extrabold text-[#e57d1c] dark:text-[#FF9A3C] mb-1">
        {numberToMoney(plan.price)}/mês
      </p>
      <p className="text-sm text-[#121516]/70 dark:text-white/70 mb-4">
        {numberToMoney(plan.price * 12)}/ano
      </p>
      <ul className="mb-6 space-y-2">
        {allFeatures.map((featureName) => {
          const feature = plan.features.find((f) => f.name === featureName);
          return (
            <li key={featureName} className="flex items-start">
              <span className="text-[#121516]/80 dark:text-white/80 flex items-center">
                <strong className="mr-2">
                  {feature?.displayName || featureName}:
                </strong>
                <FeatureValue feature={feature} />
              </span>
            </li>
          );
        })}
      </ul>
      <button
        className="w-full bg-gradient-to-r from-[#FF9A3C] to-[#EE7B11] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg hover:shadow-[#EE7B11]/30 transition-all duration-300 transform hover:-translate-y-1"
        onClick={() => loginWithRedirect()}
      >
        ESCOLHER PLANO
      </button>
    </div>
  );
};

const DesktopPricingTable = ({
  plans,
  allFeatures,
}: {
  plans: PlansOutput[];
  allFeatures: string[];
}) => {
  const { loginWithRedirect } = useAuth0();

  return (
    <div className="overflow-x-auto rounded-lg shadow-md">
      <table className="w-full bg-white dark:bg-gray-800">
        <thead>
          <tr className="bg-[#f0f0f0] dark:bg-gray-700">
            <th className="p-4 text-left text-[#121516] dark:text-white font-bold rounded-tl-lg">
              Recursos
            </th>
            {plans.map((plan, index) => (
              <th
                key={plan.id}
                className={`p-4 text-center ${
                  index === plans.length - 1 ? "rounded-tr-lg" : ""
                }`}
              >
                <h3 className="text-xl font-bold text-[#121516] dark:text-white">
                  {plan.name}
                </h3>
                <p className="text-2xl font-extrabold text-[#e57d1c] dark:text-[#FF9A3C] mb-1">
                  {numberToMoney(plan.price)}/mês
                </p>
                <p className="text-xs text-[#121516]/70 dark:text-white/70">
                  {numberToMoney(plan.price * 12)}/ano
                </p>
                <button
                  className="mt-4 bg-gradient-to-r from-[#FF9A3C] to-[#EE7B11] text-white px-6 py-2 rounded-full font-bold hover:shadow-lg hover:shadow-[#EE7B11]/30 transition-all duration-300 transform hover:-translate-y-1 text-sm"
                  onClick={() => loginWithRedirect()}
                >
                  ESCOLHER PLANO
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allFeatures.map((featureName, featureIndex) => {
            const featureDisplayName =
              plans
                .find((plan) =>
                  plan.features.find((f) => f.name === featureName)
                )
                ?.features.find((f) => f.name === featureName)?.displayName ||
              featureName;

            const isLastRow = featureIndex === allFeatures.length - 1;

            return (
              <tr key={featureName} className="border-t dark:border-gray-700">
                <td
                  className={`p-4 font-semibold text-[#121516] dark:text-white ${
                    isLastRow ? "rounded-bl-lg" : ""
                  }`}
                >
                  {featureDisplayName}
                </td>
                {plans.map((plan, planIndex) => (
                  <td
                    key={`${plan.id}-${featureName}`}
                    className={`p-4 text-center ${
                      isLastRow && planIndex === plans.length - 1
                        ? "rounded-br-lg"
                        : ""
                    }`}
                  >
                    <FeatureValue
                      feature={plan.features.find(
                        (f) => f.name === featureName
                      )}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PricingSkeleton = () => (
  <div className="space-y-8">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <div className="h-8 w-1/3 mb-2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="h-10 w-1/2 mb-1 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="h-4 w-1/4 mb-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        <div className="space-y-2 mb-6">
          {[1, 2, 3, 4, 5].map((j) => (
            <div
              key={j}
              className="h-6 w-full bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            ></div>
          ))}
        </div>
        <div className="h-12 w-full rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
      </div>
    ))}
  </div>
);

export function PricingApi() {
  const [plans, setPlans] = useState<PlansOutput[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const planService = new PlansService();
        const { data: fetchedPlans } = await planService.findAll({
          expand: ["features"],
        });
        setPlans(fetchedPlans);
      } catch (error) {
        console.error("Error fetching plans:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return (
    <section id="pricing" className="py-20 px-4 container mx-auto scroll-mt-24">
      <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">
        PLANOS QUE{" "}
        <span className="text-gray-900 dark:text-gray-200 font-black">
          CABEM NO SEU BOLSO
        </span>
      </h2>
      <p className="text-white/80 text-center mb-12 text-lg">
        Escolha o plano ideal para o seu negócio
      </p>

      {loading ? (
        <PricingSkeleton />
      ) : plans.length === 0 ? (
        <div className="flex justify-center items-center h-64 bg-white dark:bg-gray-800 rounded-lg">
          <span className="text-xl font-semibold text-[#121516] dark:text-white">
            Nenhum plano disponível.
          </span>
        </div>
      ) : (
        <>
          {/* Mobile view */}
          <div className="md:hidden space-y-8">
            {plans.map((plan, index) => (
              <MobilePlanCard
                key={plan.id}
                plan={plan}
                allFeatures={Array.from(
                  new Set(plans.flatMap((p) => p.features.map((f) => f.name)))
                )}
                index={index}
              />
            ))}
          </div>

          {/* Desktop view */}
          <div className="hidden md:block">
            <DesktopPricingTable
              plans={plans}
              allFeatures={Array.from(
                new Set(plans.flatMap((p) => p.features.map((f) => f.name)))
              )}
            />
          </div>
        </>
      )}
    </section>
  );
}
